/** @module Opening, stopping and grabbing frames from the camera; plain DOM, no React. */
import { withTimeout } from "@/lib/timeout";

/**
 * Width of the uploaded photo.
 *
 * The detector works on 1600, but the card number does not: the OCR reads a few
 * millimetres of print, and the collector's 3072 px photos are what made it work.
 */
export const CAPTURE_WIDTH = 3072;

/**
 * Width to ask the camera for; it answers with the best it has.
 *
 * Without it most phone browsers deliver 640 x 480, and the photo is cut from that video:
 * the app's own scans arrived at 381 x 590 px, far too small to read the card number.
 */
export const CAMERA_WIDTH = 3840;

export interface CameraOptions {
  /** Width to ask the camera for; it answers with the closest it can deliver. */
  idealWidth?: number;
  /** Which camera to use; phones have several and the default one is often narrow. */
  deviceId?: string;
}

/**
 * The cameras this device offers, once permission was given.
 *
 * Before permission the labels are empty, which is why this is called after the
 * stream is running. On an iPhone the list holds the ultra wide lens as well, and
 * that is the one a holder needs: the standard lens is far too narrow up close.
 */
export async function cameras(): Promise<MediaDeviceInfo[]> {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.filter((device) => device.kind === "videoinput");
  } catch {
    return [];
  }
}

/** Neither torch nor zoom are in the DOM typings yet; Chrome on Android has both. */
type ExtraCapabilities = MediaTrackCapabilities & {
  torch?: boolean;
  zoom?: { min: number; max: number; step: number };
};

export class Camera {
  private stream: MediaStream | null = null;

  /** Ask for the rear camera and show it in the given element. */
  async start(element: HTMLVideoElement, options: CameraOptions = {}): Promise<void> {
    // No resolution is asked for on purpose: a phone that has to deliver an exact size
    // often crops the sensor to get there, and the picture arrives zoomed in.
    const video: MediaTrackConstraints = options.deviceId
      ? { deviceId: { exact: options.deviceId } }
      : { facingMode: { ideal: "environment" } };
    if (options.idealWidth) video.width = { ideal: options.idealWidth };
    const stream = await navigator.mediaDevices.getUserMedia({ video, audio: false });
    this.stream = stream;
    element.srcObject = stream;
    await element.play();
    await this.widest();
  }

  /** Zoom all the way out where the browser allows it; the holder sits close. */
  private async widest(): Promise<void> {
    try {
      const capabilities: ExtraCapabilities | undefined = this.track?.getCapabilities();
      const zoom = capabilities?.zoom;
      if (zoom && zoom.min < zoom.max) {
        await this.track?.applyConstraints({ advanced: [{ zoom: zoom.min } as never] });
      }
    } catch {
      // Safari has no zoom constraint at all; then the camera is as wide as it gets.
    }
  }

  private get track(): MediaStreamTrack | undefined {
    return this.stream?.getVideoTracks()[0];
  }

  /** Whether the lamp can be switched from the browser; Safari on iOS cannot. */
  get hasTorch(): boolean {
    try {
      const capabilities: ExtraCapabilities | undefined = this.track?.getCapabilities();
      return capabilities?.torch === true;
    } catch {
      // Older Safari has no getCapabilities at all, the typings notwithstanding.
      return false;
    }
  }

  async setTorch(on: boolean): Promise<void> {
    await this.track?.applyConstraints({ advanced: [{ torch: on } as MediaTrackConstraintSet] });
  }

  stop(element: HTMLVideoElement | null): void {
    this.stream?.getTracks().forEach((track) => {
      track.stop();
    });
    this.stream = null;
    if (element) element.srcObject = null;
  }
}

export interface Frame {
  photo: Blob;
  /** Size of the photo, not of the video: the still is scaled down before upload. */
  width: number;
  height: number;
}

/** Reading the picture out of the canvas must not stall the button either. */
const BLOB_TIMEOUT_MS = 4000;

/**
 * Take a still from the running video; null while no frame has arrived yet.
 *
 * The cut-out is what the person sees: with a digital zoom the photo has to show the
 * same thing, otherwise the frame on screen would promise something else.
 */
export async function grabFrame(
  element: HTMLVideoElement | null,
  maxWidth: number = CAPTURE_WIDTH,
  quality = 0.85,
  cut?: { x: number; y: number; width: number; height: number },
): Promise<Frame | null> {
  // Below HAVE_CURRENT_DATA the element has a size but no picture: the photo comes out black.
  if (!element || element.videoWidth === 0 || element.readyState < 2) return null;
  const source = {
    x: Math.round((cut?.x ?? 0) * element.videoWidth),
    y: Math.round((cut?.y ?? 0) * element.videoHeight),
    width: Math.round((cut?.width ?? 1) * element.videoWidth),
    height: Math.round((cut?.height ?? 1) * element.videoHeight),
  };
  const scale = Math.min(1, maxWidth / source.width);
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(source.width * scale);
  canvas.height = Math.round(source.height * scale);
  const context = canvas.getContext("2d");
  if (!context) return null;
  context.drawImage(
    element,
    source.x,
    source.y,
    source.width,
    source.height,
    0,
    0,
    canvas.width,
    canvas.height,
  );
  const encoded = new Promise<Frame | null>((resolve) => {
    canvas.toBlob(
      (blob) => {
        resolve(blob ? { photo: blob, width: canvas.width, height: canvas.height } : null);
      },
      "image/jpeg",
      quality,
    );
  });
  return withTimeout(encoded, BLOB_TIMEOUT_MS, "canvas.toBlob");
}
